<script setup lang="ts">
const page = {
  title: 'Cronr - Cron Job Monitoring',
  description: 'Monitor your cron jobs with ease',
  hero: {
    title: 'Monitor your cron jobs with ease',
    description: 'Get notified when your cron jobs fail or take too long to complete.',
    links: [
      {
        label: 'Get Started',
        icon: 'i-lucide-rocket',
        to: '/signup',
        color: 'primary' as const
      },
      {
        label: 'View Pricing',
        icon: 'i-lucide-credit-card',
        to: '/pricing',
        color: 'secondary' as const
      }
    ]
  },
  features: {
    title: 'Everything You Need for Reliable Job Scheduling',
    description: 'From simple scheduling to advanced monitoring, Cronr.app provides all the tools you need to ensure your critical tasks run smoothly.',
    items: [
      {
        title: 'Simple Job Scheduling',
        description: 'Schedule jobs using intuitive time intervals or cron expressions. No complex infrastructure or DevOps required.',
        icon: 'i-lucide-calendar'
      },
      {
        title: 'Real-time Monitoring',
        description: 'Get instant alerts via email, Slack, or webhooks when jobs fail or run longer than expected.',
        icon: 'i-lucide-bell'
      },
      {
        title: 'Detailed Logging',
        description: 'Access comprehensive logs and execution history for all your jobs in one organized dashboard.',
        icon: 'i-lucide-file-text'
      },
      {
        title: 'Team Collaboration',
        description: 'Invite team members, set permissions, and collaborate effectively with shared dashboards.',
        icon: 'i-lucide-users'
      },
      {
        title: 'Performance Metrics',
        description: 'Track execution times, success rates, and other critical metrics to optimize your jobs.',
        icon: 'i-lucide-bar-chart'
      },
      {
        title: 'Global Reliability',
        description: 'Built on enterprise-grade infrastructure with guaranteed uptime and worldwide availability.',
        icon: 'i-lucide-globe'
      }
    ]
  },
  cta: {
    title: 'Start Monitoring Your Cron Jobs Today',
    description: 'Join thousands of developers who trust Cronr.app for reliable job scheduling and monitoring. Free plan available to get started.',
    links: [
      {
        label: 'Get Started',
        icon: 'i-lucide-rocket',
        to: '/signup',
        color: 'primary' as const
      }
    ]
  }
}

useSeoMeta({
  titleTemplate: '',
  title: page.title,
  ogTitle: page.title,
  description: page.description,
  ogDescription: page.description
})
</script>

<template>
  <div>
    <UPageHero
      :title="page.hero.title"
      :description="page.hero.description"
      :links="page.hero.links"
    >
      <template #top>
        <div class="absolute rounded-full dark:bg-(--ui-primary) blur-[300px] size-60 sm:size-80 transform -translate-x-1/2 left-1/2 -translate-y-80" />
        <StarsBg />
      </template>
    </UPageHero>
    <UPageSection
      :title="page.features.title"
      :description="page.features.description"
    >
      <UPageGrid>
        <UPageCard
          v-for="(item, index) in page.features.items"
          :key="index"
          v-bind="item"
          spotlight
        />
      </UPageGrid>
    </UPageSection>

    <USeparator />

    <UPageCTA
      v-bind="page.cta"
      variant="naked"
      class="overflow-hidden"
    >
      <div class="absolute rounded-full dark:bg-(--ui-primary) blur-[250px] size-40 sm:size-50 transform -translate-x-1/2 left-1/2 -translate-y-80" />

      <StarsBg />
    </UPageCTA>
  </div>
</template>
